import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useTheme } from '@mui/material/styles';

import {
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';

import AppButton, {
  AppCancelButton,
} from '../../../../components/designe/buttons/app-button/AppButton';
import {
  errorToastMessage,
  successToastMessage,
} from '../../../../components/layout/toast-root/toast';
import { useNavigationStack } from '../../../../context/NavigationStackContext';
import config from '../../../../envConfig';
import { usersApi } from '../../../../redux/api/users/usersApi';
import { USERS } from '../../../../redux/constants';
import { Paths } from '../../../../shared/paths';
import { useStyles } from './PaymentWrapper.styles';

interface PaymentWrapperProps {
  onCancel: () => void;
  onPaymentElementLoadError?: () => void;
  /** Subscription vs one-time donation — affects success copy and primary button label. */
  checkoutKind?: 'subscription' | 'donation';
}

export default function PaymentWrapper(props: PaymentWrapperProps) {
  const {
    onCancel,
    checkoutKind = 'subscription',
    onPaymentElementLoadError,
  } = props;
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();

  const { classes } = useStyles(theme);
  const { navigate } = useNavigationStack();
  const [paymentProcessing, setPaymentProcessing] = useState(false);

  function onSubmitClicked(event) {
    if (!stripe || !elements) {
      return;
    }
    setPaymentProcessing(true);
    stripe
      .confirmPayment({
        elements,
        confirmParams: {
          return_url: config.paymentReturnUrl,
        },
        redirect: 'if_required',
      })
      .then((result) => {
        if (result.error) {
          errorToastMessage(
            t('subscriptionPlan.subscriptionCreatedErrorMessage'),
          );
          return;
        }
        dispatch(usersApi.util.invalidateTags([USERS.ME, USERS.ME_DETAILED]));
        if (checkoutKind === 'donation') {
          successToastMessage(t('subscriptionPlan.donationThankYouMessage'), {
            autoClose: 4000,
          });
        } else {
          successToastMessage(
            <span style={{ whiteSpace: 'pre-line' }}>
              {t('subscriptionPlan.paymentProcessingFollowUpMessage')}
            </span>,
            { autoClose: 12000 },
          );
        }
        navigate(Paths.root);
      })
      .catch(() => {
        errorToastMessage(
          t('subscriptionPlan.subscriptionCreatedErrorMessage'),
        );
      })
      .finally(() => {
        setPaymentProcessing(false);
      });

    event.preventDefault();
  }

  return (
    <form id="payment-form" className={classes.form} onSubmit={onSubmitClicked}>
      {/*<LinkAuthenticationElement id="link-authentication-element"/>*/}
      <PaymentElement
        id="payment-element"
        options={{
          layout: 'tabs',
          wallets: {
            applePay: 'never',
            googlePay: 'never',
            link: 'never',
          },
        }}
        onLoadError={onPaymentElementLoadError}
      />
      <div className={classes.bottomContainer}>
        <AppCancelButton
          disabled={paymentProcessing}
          onClick={() => onCancel()}>
          {t('common.cancel')}
        </AppCancelButton>

        <AppButton id="submit" type="submit" loading={paymentProcessing}>
          {checkoutKind === 'donation'
            ? t('subscriptionPlan.donationSupportButton')
            : t('subscriptionPlan.subscribeToPlan')}
        </AppButton>
      </div>
    </form>
  );
}
